import { useState } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  SelectChangeEvent,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { useSiteStore } from '../store/site.store';
import { useQueryClient } from '@tanstack/react-query';
import { pagesService } from '../services/pagesService';

export const SiteSelector = () => {
  const { selectedSite, sites, isLoading, setSite } = useSiteStore();
  const queryClient = useQueryClient();
  const [pendingSite, setPendingSite] = useState<typeof sites[0] | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleSiteChange = (event: SelectChangeEvent<number>) => {
    const siteId = Number(event.target.value);
    const site = sites.find(s => s.id === siteId);
    if (!site) return;

    // If switching to different site, show confirmation
    if (selectedSite && selectedSite.id !== siteId) {
      setPendingSite(site);
      setShowConfirmDialog(true);
    } else {
      // First time selection or same site
      console.log('🔄 [SiteSelector] Setting site:', site.id, site.name);
      setSite(site, queryClient);
      // FORCE FETCH pages for the new site using fetchQuery (always executes)
      console.log('📄 [SiteSelector] Force fetching pages for site:', site.id);
      queryClient.fetchQuery({
        queryKey: ['pages-for-site', site.id],
        queryFn: async () => {
          const data = await pagesService.getPagesBySite(site.id);
          console.log('✅ [SiteSelector] Pages fetched:', data.length, 'pages for site', site.id);
          return data.map((p: any) => ({
            id: p.id,
            url: p.url || `Page #${p.id}`,
            label: p.url || `Page #${p.id}`,
          }));
        },
        staleTime: 0,
      });
    }
  };

  const handleConfirmSwitch = () => {
    if (pendingSite) {
      console.log('🔄 [SiteSelector] Switching to site:', pendingSite.id, pendingSite.name);
      setSite(pendingSite, queryClient);
      // FORCE FETCH pages for the new site using fetchQuery (always executes)
      console.log('📄 [SiteSelector] Force fetching pages for site:', pendingSite.id);
      queryClient.fetchQuery({
        queryKey: ['pages-for-site', pendingSite.id],
        queryFn: async () => {
          const data = await pagesService.getPagesBySite(pendingSite.id);
          console.log('✅ [SiteSelector] Pages fetched:', data.length, 'pages for site', pendingSite.id);
          return data.map((p: any) => ({
            id: p.id,
            url: p.url || `Page #${p.id}`,
            label: p.url || `Page #${p.id}`,
          }));
        },
        staleTime: 0,
      });
      setShowConfirmDialog(false);
      setPendingSite(null);
    }
  };

  const handleCancelSwitch = () => {
    setShowConfirmDialog(false);
    setPendingSite(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
        <CircularProgress size={24} sx={{ color: 'inherit' }} />
      </Box>
    );
  }

  if (sites.length === 0) {
    return (
      <Typography variant="body2" color="inherit" sx={{ px: 2, opacity: 0.7 }}>
        No sites available
      </Typography>
    );
  }

  return (
    <>
      <FormControl size="small" sx={{ minWidth: 250 }}>
        <Select
          value={selectedSite?.id || ''}
          onChange={handleSiteChange}
          displayEmpty
          startAdornment={<BusinessIcon sx={{ mr: 1, color: 'inherit', opacity: 0.8 }} />}
          sx={{
            color: 'inherit',
            '.MuiOutlinedInput-notchedOutline': { border: 0 },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
            '&:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
            '.MuiSelect-icon': { color: 'inherit' },
          }}
          renderValue={(selected) => {
            if (!selected) {
              return <Typography variant="body2" sx={{ opacity: 0.7 }}>Select a site...</Typography>;
            }
            const site = sites.find(s => s.id === selected);
            return (
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: 'inherit' }}>
                  {site?.name}
                </Typography>
              </Box>
            );
          }}
        >
          {sites.map((site) => (
            <MenuItem key={site.id} value={site.id}>
              <Box>
                <Typography variant="body2" fontWeight={selectedSite?.id === site.id ? 600 : 400}>
                  {site.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {site.url}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Site Switch Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={handleCancelSwitch}>
        <DialogTitle>Switch Site?</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to switch from <strong>{selectedSite?.name}</strong> to{' '}
            <strong>{pendingSite?.name}</strong>.
          </Typography>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Current page data will be cleared and new data will be loaded for the selected site.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSwitch}>Cancel</Button>
          <Button onClick={handleConfirmSwitch} variant="contained" autoFocus>
            Switch Site
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
